import React from 'react';
import {
  ActionIcon, CardSkeleton, ICON_CHECK, Message, Stack, Text, SimpleTable,
} from '@doveit/bricks';
import { Reference, SuggestedContactReferenceType } from '../../../domain/types';
import { SuggestedContact } from '../../../providers/api/dtos';
import useSuggestedContacts from '../../hooks/use-suggested-contacts/useSuggestedContacts';
import useIsDevice from '../../../hooks/use-is-device/useIsDevice';
import ContactCard from '../../components/contact-card/ContactCard';
import ContactTableRow from '../../components/contact-table-row/ContactTableRow';
import { contactRoleLabel } from '../../constants';

export interface ViewSuggestedContactsProps {
  reference: Reference<SuggestedContactReferenceType>,
  onSelect: (contact: SuggestedContact) => void,
}

const ViewSuggestedContacts: React.FC<ViewSuggestedContactsProps> = ({
  reference,
  onSelect,
}) => {
  const isMobile = useIsDevice('mobile');

  const {
    data: contacts,
    isLoading: areContactsLoading,
    error: contactsError,
  } = useSuggestedContacts(reference, {
    revalidateOnMount: true,
  });

  const sortedContacts = React.useMemo(() => (contacts || [])
    .slice()
    .sort((a, b) => {
      if (a.role === b.role) {
        const aName = a.contact.name;
        const bName = b.contact.name;
        if (aName && bName) {
          return aName.localeCompare(bName, 'it');
        }

        return 0;
      }
      if (a.role === 'BUYER') return -1;
      if (b.role === 'BUYER') return 1;
      return 0;
    }), [contacts]);

  if (areContactsLoading) {
    return <CardSkeleton aria-label="Suggerimenti in caricamento" />;
  }

  if (contactsError || !contacts?.length) {
    return null;
  }

  return (
    <Message
      message="Suggerimenti"
      boxed
    >
      <Stack gap={200}>
        <Text.BodySmall>
          Clicca il suggerimento se presente nella lista oppure &quot;Cerca o aggiungi&quot;.
        </Text.BodySmall>

        {isMobile && (
          <Stack aria-label="Lista suggerimenti">
            {sortedContacts.map(({ contact, role }) => (
              <ContactCard
                key={contact.id}
                contact={contact}
                details={role ? contactRoleLabel[role] : undefined}
                primaryAction={(
                  <ActionIcon
                    size="S"
                    icon={{ path: ICON_CHECK }}
                    label="Seleziona suggerimento"
                    onClick={() => onSelect({ contact, role })}
                  />
                )}
              />
            ))}
          </Stack>
        )}

        {!isMobile && (
          <SimpleTable aria-label="Lista suggerimenti">
            <SimpleTable.Body>
              {sortedContacts.map(({ contact, role }) => (
                <SimpleTable.Row
                  key={contact.id}
                  asChild
                >
                  <ContactTableRow
                    contact={contact}
                    details={role ? contactRoleLabel[role] : undefined}
                    primaryAction={(
                      <ActionIcon
                        size="S"
                        icon={{ path: ICON_CHECK }}
                        label="Seleziona suggerimento"
                        onClick={() => onSelect({ contact, role })}
                      />
                    )}
                  />
                </SimpleTable.Row>
              ))}
            </SimpleTable.Body>
          </SimpleTable>
        )}
      </Stack>
    </Message>
  );
};

export default ViewSuggestedContacts;
